// Pure helpers for the one-tap site report (one visit, printable / save as PDF).
// Everything comes from the timeline the page already has; nothing is invented.
import { KIND_LABEL, CAPTURE_LABEL, compareFrames, daysApart, humanDate, galleryItems, visitStatus } from './siteTimelineModel';

export const MAX_REPORT_PHOTOS = 9;

// A report needs something to say: a delivered file or a progress note.
export function canReport(v) {
  if (!v || v.status === 'failed') return false;
  const hasNote = Boolean(v.progress_note && String(v.progress_note.text || '').trim());
  return Boolean((v.deliverables || []).length || hasNote);
}

// The newest earlier visit that actually happened (dated, not failed).
export function previousVisit(visits, visit) {
  if (!visit || !visit.date) return null;
  let best = null;
  (visits || []).forEach((x) => {
    if (!x || x.id === visit.id || !x.date || x.status === 'failed') return;
    if (x.date < visit.date && (!best || x.date > best.date)) best = x;
  });
  return best;
}

// "Sep 12, 2026 · 28 days earlier" or "First visit on this site".
export function sinceLine(prev, visit) {
  if (!prev) return 'First visit on this site';
  const n = daysApart(prev.date, visit && visit.date);
  return `${humanDate(prev.date)}${n !== null ? ` · ${n} day${n === 1 ? '' : 's'} earlier` : ''}`;
}

function host(url) {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return ''; }
}

// Then / now picture pair for the first compare kind both visits carry.
export function reportCompare(visits, visit, prev, kinds) {
  if (!visit || !prev) return null;
  for (const kind of kinds || []) {
    const frames = compareFrames(visits, kind);
    const now = frames.find((f) => f.visitId === visit.id);
    const then = frames.find((f) => f.visitId === prev.id);
    if (now && then && now.src && then.src) {
      return { kind, label: KIND_LABEL[kind] || 'File', then: { src: then.src, date: then.date }, now: { src: now.src, date: now.date } };
    }
  }
  return null;
}

// Everything the report page renders for one visit.
export function buildReport(timeline, visitId, project, phases) {
  const visits = (timeline && timeline.visits) || [];
  const visit = visits.find((v) => v.id === visitId);
  if (!visit) return null;
  const prev = previousVisit(visits, visit);
  const photos = galleryItems(visit).filter((m) => m.type === 'image');
  const files = (visit.deliverables || []).filter((d) => d.url && !(d.scan && d.scan.status === 'queued')).map((d, i) => ({
    id: d.id || `${visit.id}-${i}`,
    kind: d.kind,
    kindLabel: KIND_LABEL[d.kind] || 'File',
    label: d.label || KIND_LABEL[d.kind] || 'File',
    url: d.url,
    host: host(d.url),
  }));
  const pending = (visit.deliverables || []).filter((d) => d.scan && d.scan.status === 'queued').length;
  const phase = visit.phase_name || (visit.phase_id ? ((phases || []).find((p) => p.id === visit.phase_id) || {}).name || null : null);
  const note = visit.progress_note && String(visit.progress_note.text || '').trim() ? visit.progress_note : null;
  return {
    project: { name: (project && project.name) || 'Site', place: [project && project.address, project && project.city].filter(Boolean).join(', ') },
    date: humanDate(visit.date) || 'Date to be set',
    capture: (visit.source && visit.source.label) || CAPTURE_LABEL[visit.capture] || 'Capture visit',
    credit: visit.source ? 'Prepared with Bricks' : 'Captured and delivered by Bricks',
    phase,
    status: visitStatus(visit).label,
    since: sinceLine(prev, visit),
    note,
    compare: reportCompare(visits, visit, prev, timeline && timeline.compare_kinds),
    photos: photos.slice(0, MAX_REPORT_PHOTOS),
    morePhotos: Math.max(0, photos.length - MAX_REPORT_PHOTOS),
    files,
    pending,
  };
}

// "site-report-123-main-st-2026-10-10" for the saved PDF's title.
export function reportTitle(project, visit) {
  const slug = String((project && project.name) || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'site';
  return `Site report ${slug}${visit && visit.date ? ` ${visit.date}` : ''}`;
}

// Footer credit for a set of visits: never claims Bricks flew an imported capture.
export function creditLine(visits) {
  const have = (visits || []).filter((v) => (v.deliverables || []).length);
  const imported = have.filter((v) => v.source);
  if (!imported.length) return 'Captured and delivered by Bricks';
  return imported.length === have.length ? 'Prepared with Bricks' : 'Captured by Bricks, plus imported captures as marked';
}
